import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../components/customTable";
import DateRangeFilter, { rangeFromPreset } from "../../../components/dateRangeFilter/DateRangeFilter";
import useAuth from "../../../hooks/useAuth";
import { useUrlState, useUrlStateBatch } from "../../../hooks/useUrlState";
import {
  getAllPackages,
  getClientPackagesByStatusCount,
  getClientReturnsCount,
  RETURN_STATUSES,
} from "../../../api/packages";
import {
  packageStatus,
  packageStatusStyles,
} from "../../../utils/packageStatus";
import { showToast } from "../../../utils/showToast";
import { apiErrorMessage } from "../../../utils/apiErrorMessage";
import i18n from "../../../i18n/index";
import { SummaryCardsSkeleton } from "../../../components/ui/SummaryCardsSkeleton";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import { Button } from "../../../components/ui/Button";
import EvidenceView from "../../../components/evidenceView";
import { AlertTriangle, RefreshCw } from "lucide-react";
import moment from "moment";
import Spinner from "../../../components/spinner/spinner";
import PendingAddressChip from "../../../components/addressPicker/PendingAddressChip";

// Identificadores estables de las KPI-cards-como-filtro (para ?card= en la URL).
// Espejo de la vista admin (resume): status estático; título/valor en render.
const CARD_DEFS = [
  { key: "created", status: [0] },
  { key: "ready", status: [100] },
  { key: "picked", status: [1] },
  { key: "route", status: [3, 30] },
  { key: "delivered", status: [4] },
  { key: "failed", status: [6, 7] },
  // Logística inversa: recibido para devolución (21), en bodega (22),
  // en ruta de devolución (31) y devolución completada (41).
  { key: "returns", status: RETURN_STATUSES },
];

// Reconstruye `filters` desde los params de la URL (card/status/type) + el rango
// vigente. En esta vista `filters` SIEMPRE lleva fechas (la tabla filtra por
// dateStart/dateEnd), así que el default (sin card/status/type) es { dateStart,
// dateEnd } — idéntico al comportamiento actual.
function buildFiltersFromUrl(cardParam, statusParam, typeParam, dateStart, dateEnd) {
  const f = { dateStart, dateEnd };
  if (cardParam) {
    const def = CARD_DEFS.find((c) => c.key === cardParam);
    if (def) {
      f.status = def.status;
      return f;
    }
  }
  if (statusParam && statusParam.length) f.status = statusParam.map(Number);
  if (typeParam) f.type = typeParam;
  return f;
}

export default function AllOrders({ update, resetUpdate }) {
  const { t } = useTranslation();
  const { userData, isLoading } = useAuth();
  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // Falló la carga (reset) del listado: se muestra un aviso de error en lugar
  // del estado vacío "¿Buscas un pedido antiguo?", que lo haría pasar por
  // "no hay pedidos".
  const [loadError, setLoadError] = useState(false);
  const [updateData, setUpdateData] = useState(false);
  const [viewEvidence, setViewEvidence] = useState(false);
  const [packageEvidence, setPackageEvidence] = useState({});
  // Prefiltro de fechas: mismo control que la vista admin (DateRangeFilter,
  // presets Hoy/Ayer/7 días/30 días/Este mes + "Más"). Por defecto "7d" como
  // admin. showResume (MM/DD/YYYY) alimenta las cards de KPIs; filters.dateStart
  // /dateEnd (YYYY-MM-DD) alimenta la query de la tabla.
  const DEFAULT_PRESET = "7d";
  // URL-state: filtros compartibles + back/forward, sembrados de forma SÍNCRONA
  // ANTES del primer fetch. Sin params ⇒ cada default == el de hoy ⇒ comportamiento
  // idéntico. Espejo 1:1 de la vista admin (resume).
  const [qParam, setQParam] = useUrlState("q", "");
  // Lectura de preset/from/to; la ESCRITURA de los TRES va JUNTA con
  // useUrlStateBatch (handleRangeChange). Con setters separados de useUrlState las
  // navegaciones se pisaban: react-router no encadena dos setSearchParams
  // síncronos, así que cada uno leía el querystring previo (sin ver los otros) y
  // solo sobrevivía el último → preset/from quedaban pegados en su default.
  const [datePreset] = useUrlState("preset", DEFAULT_PRESET);
  // Rango 'custom': persistido como ?from/?to (YMD) junto a ?preset=custom para
  // que al recargar/compartir se reconstruya en vez de colapsar a HOY.
  const [fromParam] = useUrlState("from", "");
  const [toParam] = useUrlState("to", "");
  const setRange = useUrlStateBatch({
    preset: { type: "string", defaultValue: DEFAULT_PRESET },
    from: { type: "string", defaultValue: "" },
    to: { type: "string", defaultValue: "" },
  });
  const [statusParam, setStatusParam] = useUrlState("status", [], { type: "csv" });
  const [typeParam, setTypeParam] = useUrlState("type", "");
  const [cardParam, setCardParam] = useUrlState("card", "");

  const [query, setQuery] = useState(() => ({ type: "", query: qParam }));
  const [filters, setFilters] = useState(() => {
    const [s, e] =
      datePreset === "custom" && fromParam && toParam
        ? [moment(fromParam, "YYYY-MM-DD"), moment(toParam, "YYYY-MM-DD")]
        : rangeFromPreset(datePreset);
    return buildFiltersFromUrl(
      cardParam,
      statusParam,
      typeParam,
      s.format("YYYY-MM-DD"),
      e.format("YYYY-MM-DD")
    );
  });
  const [showResume, setShowResume] = useState(() => {
    const [s, e] =
      datePreset === "custom" && fromParam && toParam
        ? [moment(fromParam, "YYYY-MM-DD"), moment(toParam, "YYYY-MM-DD")]
        : rangeFromPreset(datePreset);
    return { startDate: s.format("MM/DD/YYYY"), endDate: e.format("MM/DD/YYYY") };
  });
  const showResumeRef = useRef(showResume);
  showResumeRef.current = showResume;
  // Ampliación incremental del estado vacío: cada click suma 30 días hacia atrás
  // al inicio del rango (espejo de /admin/pedidos). Guarda el nº de ampliaciones
  // aplicadas (0 = sin ampliar) y un tope para no buscar indefinidamente.
  const MAX_DATE_EXPANSIONS = 6; // ≈ 180 días (~6 meses)
  const DATE_EXPANSION_DAYS = 30;
  const [dateExpansions, setDateExpansions] = useState(0);
  // Rango concreto (YYYY-MM-DD) mientras se amplía, para reflejarlo en el resumen
  // del DateRangeFilter (modo "custom").
  const [expandedRange, setExpandedRange] = useState(null);
  // Inicio del rango ANTES de la primera ampliación (moment). Se fija en el primer
  // click y ancla cada +30 días para no acumular errores de redondeo.
  const expandBaseStartRef = useRef(null);
  const [created, setCreated] = useState(0);
  const [readyToDispatch, setReadyToDispatch] = useState(0);
  const [picked, setPicked] = useState(0);
  const [inRoute, setInRoute] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [failed, setFailed] = useState(0);
  const [returns, setReturns] = useState(0);
  const [updateCards, setUpdateCards] = useState(false);
  const [selectedCard, setSelectedCard] = useState(() => {
    if (!cardParam) return null;
    const def = CARD_DEFS.find((c) => c.key === cardParam);
    return def ? { key: def.key, filter: { status: def.status } } : null;
  });
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  const height = window.visualViewport?.height || window.innerHeight || 800;
  const totalDocsPerPage = Math.floor((height * 0.4) / 55 - 2);
  const docsPerPage = Math.max(totalDocsPerPage || 6, 20); // Aumentar a 20 para scroll infinito más fluido

  // Optional chaining completo: una cuenta sin companyId (p.ej. logistic/admin
  // entrando a /home) reventaba con TypeError → error boundary.
  const companyId = userData?.companyId?._id

  // Función para cargar datos (primera carga o recarga)
  const loadData = useCallback(async (page = 1, reset = false) => {
    if (!companyId || isLoading) return;

    // El backend valida `q` con @MinLength(2) @MaxLength(64): 1 carácter o más de
    // 64 devuelven 400. Se corta ANTES del reset (que vacía `data`) y sin pedir
    // nada: se conservan los datos ya cargados.
    const q = (query?.query || "").trim();
    if (q.length === 1 || q.length > 64) {
      showToast(i18n.t("La búsqueda debe tener entre 2 y 64 caracteres."), "warning");
      setLoading(false);
      setLoadingTable(false);
      setLoadingMore(false);
      return;
    }

    if (reset) {
      // Establecer loadingTable y resetear datos de forma síncrona
      setLoadingTable(true);
      setData([]);
      setTableData([]); // Resetear tableData inmediatamente para evitar mostrar datos antiguos
      setCurrentPage(1);
      setHasMore(true);
      setLoadError(false);
    } else {
      setLoadingMore(true);
    }

    setUpdateData(false);
    resetUpdate(false);

    const isBlankSearch = !q;
    const baseArgs = {
      id: companyId,
      orderField: filters?.orderBy || "createdAt",
      coms: filters?.coms,
      zipCode: filters?.zipCode,
      status: filters?.status,
      startDate: filters?.dateStart
        ? moment(filters.dateStart, "YYYY-MM-DD").format("MM/DD/YYYY")
        : undefined,
      // endDateCreation is already inclusive (end of day) in the backend: no +1 day.
      endDate: filters?.dateEnd
        ? moment(filters.dateEnd, "YYYY-MM-DD").format("MM/DD/YYYY")
        : undefined,
    };

    try {
      // Búsqueda unificada: un solo request con parámetro `q` que matchea
      // followingNumber + order_id + shipping_address.address_line con
      // coincidencia parcial en el backend. Reemplaza el patrón anterior de
      // 3 requests paralelos + dedupe manual + paginación rota. El rango de
      // fechas + card de estado siguen actuando como prefiltros (baseArgs).
      const response = await getAllPackages({
        ...baseArgs,
        limit: docsPerPage,
        page: page,
        ...(isBlankSearch ? {} : { q }),
      });

      if (response.success && response.data) {
        const apiData = response.data;
        const message = apiData?.message;
        const result = apiData?.docs || [];

        if (message) {
          showToast(message, "info");
        }

        setTotalDocs(apiData?.totalDocs || 0);

        if (reset) {
          setLoadError(false);
          setData(result);
          // Si no hay resultados, establecer loadingTable en false después de establecer data
          if (result.length === 0) {
            // Usar setTimeout para asegurar que data se actualice primero
            setTimeout(() => {
              setLoadingTable(false);
            }, 0);
          }
        } else {
          setData(prevData => [...prevData, ...result]);
        }

        // Verificar si hay más páginas
        const totalPages = Math.ceil((apiData?.totalDocs || 0) / docsPerPage);
        const hasMorePages = page < totalPages;
        setHasMore(hasMorePages);
      } else {
        // Error de API. El toast con el texto del backend ya lo mostró
        // getAllPackages (no va en modo silent).
        // - reset (búsqueda/filtro nuevo): las filas ya se vaciaron al iniciar el
        //   reset, así que se deja el contador en 0 y se marca `loadError` para
        //   mostrar un aviso de error con reintento en vez del estado vacío.
        // - loadMore: se conservan las filas ya cargadas.
        // `hasMore` se corta en ambos casos: si no, el centinela del scroll
        // infinito volvería a pedir la página siguiente en bucle mientras el
        // backend siga fallando. Se rearma con el próximo reset.
        console.error("API response error:", response.error);
        setHasMore(false);
        if (reset) {
          setTotalDocs(0);
          setLoadError(true);
          setLoadingTable(false);
        }
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      showToast(
        // Module-level i18n (not the hook's t): keeps loadData's identity, and
        // so the list, stable across language changes.
        apiErrorMessage(error, i18n.t("Error al cargar los paquetes. Por favor intenta nuevamente.")),
        false
      );
      setHasMore(false);
      if (reset) {
        setTotalDocs(0);
        setLoadError(true);
        setLoadingTable(false);
      }
    } finally {
      if (reset) {
        setLoading(false);
        // No establecer loadingTable aquí, se establecerá en el useEffect que procesa los datos
      } else {
        setLoadingMore(false);
      }
    }
  }, [companyId, isLoading, docsPerPage, query, filters, resetUpdate]);

  // useEffect para carga inicial o cuando cambian filtros/búsqueda
  useEffect(() => {
    if (companyId && !isLoading) {
      // Establecer loadingTable en true ANTES de llamar a loadData para asegurar que se muestre el spinner
      setLoadingTable(true);
      setTableData([]); // Resetear tableData inmediatamente
      loadData(1, true);
    } else if (!companyId && !isLoading) {
      setLoadingTable(false);
      setLoading(false);
    }
  }, [
    companyId,
    isLoading,
    updateData,
    update,
    query,
    filters,
    loadData, // Incluir loadData en las dependencias
  ]);

  // Función para cargar más datos (scroll infinito)
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || !companyId || isLoading) {
      return;
    }
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    loadData(nextPage, false);
  }, [loadingMore, hasMore, currentPage, companyId, isLoading, loadData]);

  // Observer para detectar cuando el usuario llega al final
  useEffect(() => {
    // Limpiar observer anterior si existe
    if (observerRef.current && observerTarget.current) {
      observerRef.current.unobserve(observerTarget.current);
      observerRef.current = null;
    }

    // Solo configurar observer si hay más datos, no está cargando más, no está cargando la tabla, y hay datos cargados
    if (!hasMore || loadingMore || loadingTable || !data.length || !companyId || isLoading) return;

    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Verificar que el elemento está intersectando y las condiciones se cumplen
        if (entry.isIntersecting && hasMore && !loadingMore && !loadingTable && companyId && !isLoading) {
          loadMore();
        }
      },
      {
        root: null, // Usar viewport de la ventana
        rootMargin: '300px', // Empezar a cargar cuando está a 300px del final
        threshold: 0.01
      }
    );

    observer.observe(currentTarget);
    observerRef.current = observer;

    return () => {
      if (observerRef.current && currentTarget) {
        observerRef.current.unobserve(currentTarget);
        observerRef.current = null;
      }
    };
  }, [hasMore, loadingMore, loadingTable, companyId, isLoading, loadMore, data.length]);

  
  // Al montar, dispara el cálculo de las cards con el rango por defecto.
  useEffect(() => {
    setUpdateCards(true);
  }, []);

  // Back/forward — re-siembra la búsqueda aplicada desde ?q=. No-op en el primer
  // render (el estado ya se sembró igual), así no dispara un fetch extra.
  useEffect(() => {
    setQuery((prev) => (prev.query === qParam ? prev : { type: prev.type, query: qParam }));
  }, [qParam]);

  // Back/forward — re-siembra el rango desde la URL: ?preset=custom se reconstruye
  // desde ?from/?to; cualquier otro preset deriva de rangeFromPreset. Guard por
  // igualdad para no duplicar el fetch en el primer render ni tras
  // handleRangeChange (que ya setea showResume).
  useEffect(() => {
    let s;
    let e;
    if (datePreset === "custom") {
      if (!fromParam || !toParam) return;
      s = moment(fromParam, "YYYY-MM-DD");
      e = moment(toParam, "YYYY-MM-DD");
    } else {
      [s, e] = rangeFromPreset(datePreset);
    }
    const startDate = s.format("MM/DD/YYYY");
    const endDate = e.format("MM/DD/YYYY");
    const cur = showResumeRef.current;
    if (cur.startDate === startDate && cur.endDate === endDate) return;
    setShowResume({ startDate, endDate });
    setUpdateCards(true);
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      dateStart: s.format("YYYY-MM-DD"),
      dateEnd: e.format("YYYY-MM-DD"),
    }));
    // Navegar el rango por URL (back/forward) reinicia la ampliación incremental.
    setDateExpansions(0);
    setExpandedRange(null);
    expandBaseStartRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, fromParam, toParam]);

  // Llamada a getClientPackagesByStatusCount
  // Hacemos DOS llamadas para alinear semantica con "Calidad de entregas":
  //  1) createdAt: para los KPIs de estados intermedios (creados, recolectados,
  //     en ruta, fallidos), donde la fecha de entrega aun no aplica.
  //  2) deliveredAt: solo para sobreescribir el conteo de "entregados" usando
  //     la fecha de entrega real, que es comparable directamente con el
  //     `deliveryDate` que usa `delivery_quality_scores`.
  useEffect(() => {
    if (updateCards && companyId && !isLoading) {
      Promise.all([
        getClientPackagesByStatusCount({
          companyId,
          startDate: showResume.startDate,
          endDate: showResume.endDate,
        }),
        getClientPackagesByStatusCount({
          companyId,
          startDate: showResume.startDate,
          endDate: showResume.endDate,
          dateField: "deliveredAt",
        }),
        // Tercera llamada: la logística inversa no viene en status-count
        // (ver nota en getClientReturnsCount).
        getClientReturnsCount({
          startDate: showResume.startDate,
          endDate: showResume.endDate,
        }),
      ])
        .then(([byCreated, byDelivered, returnsCount]) => {
          setCreated(byCreated.status0);
          setReadyToDispatch(byCreated.status100);
          setPicked(byCreated.status1);
          setInRoute(byCreated.status3_30);
          setFailed(byCreated.status6_7);
          setDelivered(byDelivered.status4);
          setReturns(returnsCount);
        })
        .catch((error) => {
          console.error("Error en getClientPackagesByStatusCount:", error);
        })
        .finally(() => {
          setUpdateCards(false);
        });
    }
  }, [companyId, showResume, updateCards, isLoading]);

  // useEffect para resetear tableData cuando loadingTable se establece en true
  useEffect(() => {
    if (loadingTable) {
      setTableData([]);
    }
  }, [loadingTable]);

  // useEffect para parsear los datos acumulados
  useEffect(() => {
    // Si estamos cargando y data es null/undefined, esperar a que lleguen los datos
    if (loadingTable && !data) {
      return;
    }
    
    // Si estamos cargando y data es un array vacío, podría ser el reset inicial
    // No hacer nada aquí, se manejará en loadData cuando llegue la respuesta
    if (loadingTable && Array.isArray(data) && data.length === 0) {
      return;
    }
    
    if (data && data.length > 0) {
      const parsedData = data?.map((element) => {
      return {
        correlative: {
          value: (
            <div className="custom-center-inner-td ml-3">
              <span>{element?.followingNumber}</span>
            </div>
          ),
          type: "correlative",
          id: element._id,
          comparative: element?.followingNumber,
        },
        orderId: (
          <div className="custom-center-inner-td ml-3">
            <span>{element?.order_id}</span>
          </div>
        ),
        receiver: (
          <div className="d-grid">
            <span>{element?.receiver_name}</span>
            <span>{element?.receiver_phone}</span>
          </div>
        ),
        destiny: (
          <div className="d-grid custom-center-inner-td">
            <span className="text-truncate">
              {element?.shipping_address?.street_number != null
                ? `${element?.shipping_address?.street_name} ${element?.shipping_address?.street_number}`
                : element?.shipping_address?.address_line}
            </span>
            <span>{element?.shipping_address?.municipality?.name}, {element?.shipping_address?.city?.name}</span>
            {/* Espejo de la marca del listado del operador (resume.jsx). El
                comercio también la ve porque es quien mejor conoce la dirección
                que cargó, y puede resolverla desde "Direcciones por resolver".
                Se compara con el valor exacto: los paquetes anteriores al
                despliegue no tienen bloque `geocoding` y no son pendientes. */}
            {element?.geocoding?.status === 'pending' && <PendingAddressChip />}
          </div>
        ),
        logistics: (
          <div className="custom-center-inner-td">
            <span>
              {element?.currentCarrier?.carrierType === "commerce_fleet"
                ? element?.senderInfo?.businessName || "N/A"
                : element?.deliveryInfo?.deliveryBusinessName || "N/A"}
            </span>
            {element?.currentCarrier?.carrierType === "commerce_fleet" && (
              <span className="badge-fleet-own">{t("Flota propia")}</span>
            )}
          </div>
        ),
        ingresed: (
          <div className="custom-center-inner-td">
            <span>{moment(element?.updatedAt).format("DD/MM/YY HH:mm")}</span>
          </div>
        ),
        createdAt: (
          <div className="custom-center-inner-td">
            <span>{moment(element?.createdAt).format("DD/MM/YY HH:mm")}</span>
          </div>
        ),
        status: {
          statusPill: (
            <div className="custom-center-inner-td">
              <div className={`${packageStatusStyles[element?.status]}`}>
                {packageStatus[element?.status]}
              </div>
            </div>
          ),
          value: (
            <div className="custom-center-inner-td">{element?.status}</div>
          ),
          status: element?.status,
        },
        packageType: element?.type, // Guardar el tipo de paquete
      };
      });
      setTableData(parsedData);
      setLoadingTable(false); // Establecer loadingTable en false después de procesar los datos
    } else if (data && Array.isArray(data) && data.length === 0 && !loadingTable) {
      // Si data está vacío y no estamos cargando, significa que la carga terminó sin resultados
      setTableData([]);
      setLoadingTable(false);
    }
  }, [data, loadingTable]);

  const headers = [
    { key: "correlative", label: t("Correlativo") },
    { key: "orderId", label: t("Número de venta")},
    { key: "receiver", label: t("Cliente") },
    { key: "destiny", label: t("Dirección de entrega") },
    { key: "logistics", label: t("Logística") },
    { key: "ingresed", label: t("Última actualización") },
    { key: "createdAt", label: t("Creado") },
    { key: "status", label: t("Estado") },
  ];

  const [unappliedQuery, setUnappliedQuery] = useState({
    type: "",
    query: "",
  });

  const searchButtonAction = () => {
    // Se aplica el texto recortado (y con el mismo rango válido que exige el
    // backend): un 1 carácter / >64 no cambia el estado, así que no dispara el
    // fetch y la tabla conserva lo que ya tenía. `currentPage` se reinicia solo
    // en las ramas que disparan un reset: hacerlo en la inválida desfasaba el
    // scroll infinito (volvía a pedir la página 2 y duplicaba filas).
    const q = (unappliedQuery.query || "").trim();
    if (q === "") {
      setCurrentPage(1);
      setQuery({
        query: "",
        type: "",
      });
      setQParam("");
      return;
    }

    if (q.length === 1 || q.length > 64) {
      showToast(t("La búsqueda debe tener entre 2 y 64 caracteres."), "warning");
      return;
    }

    setCurrentPage(1);
    setQuery({ type: unappliedQuery.type, query: q });
    setQParam(q);
  };

  const searchValue = (query, queryType) => {
    setUnappliedQuery({
      type: queryType,
      query: query,
    });
  };

  const cardsTotal = created + readyToDispatch + picked + inRoute + delivered + failed + returns;
  const totalCards = [
    { title: t("Todos"), icon: "apps", tone: "brand", value: cardsTotal, isAll: true },
    { key: "created", title: t("Paquetes creados"), icon: "schedule", tone: "neutral", value: created, filter: { status: [0] } },
    { key: "ready", title: t("Listas para despachar"), icon: "qr_code_2", tone: "info", value: readyToDispatch, filter: { status: [100] } },
    { key: "picked", title: t("Paquetes recolectados"), icon: "package_2", tone: "info", value: picked, filter: { status: [1] } },
    { key: "route", title: t("Paquetes en ruta"), icon: "local_shipping", tone: "warning", value: inRoute, filter: { status: [3, 30] } },
    { key: "delivered", title: t("Paquetes entregados"), icon: "check_circle", tone: "success", value: delivered, filter: { status: [4] } },
    { key: "failed", title: t("Entrega fallida"), icon: "cancel", tone: "error", value: failed, filter: { status: [6, 7] } },
    { key: "returns", title: t("Devoluciones"), icon: "assignment_return", tone: "reverse", value: returns, filter: { status: RETURN_STATUSES } },
  ];

  const handleCardClick = (clickedCard) => {
    if (clickedCard.isAll || selectedCard?.key === clickedCard.key) {
      setSelectedCard(null);
      setFilters({
        dateStart: moment(showResume.startDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
        dateEnd: moment(showResume.endDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      });
      setCurrentPage(1);
      setCardParam("");
      setStatusParam([]);
      setTypeParam("");
      return;
    }
    setCurrentPage(1);
    setSelectedCard(clickedCard);
    // Incluir el filtro de status y también las fechas del período
    setFilters({
      ...clickedCard.filter,
      // Convertir fechas de MM/DD/YYYY a YYYY-MM-DD para el filtro
      dateStart: moment(showResume.startDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      dateEnd: moment(showResume.endDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
    });
    // La card codifica el estado en ?card=; status/type quedan para filtros de
    // columna (mutuamente excluyentes para no duplicar la fuente de verdad).
    setCardParam(clickedCard.key);
    setStatusParam([]);
    setTypeParam("");
  };

  // Cambio de rango desde el DateRangeFilter (preset rápido o personalizado).
  // Recibe fechas "YYYY-MM-DD". Igual que admin: actualiza el scope (showResume
  // → recalcula cards) y el rango de la tabla (filters), preservando el filtro
  // de estado si hay una card activa.
  const handleRangeChange = (startYMD, endYMD, preset) => {
    // preset + from/to en UNA sola navegación (useUrlStateBatch): 'custom' ⇒
    // persistir ?from/?to; cualquier otro preset ⇒ limpiarlos (vuelven a su
    // default "" y se omiten de la URL). Con tres setters separados de
    // useUrlState los setSearchParams síncronos se pisaban y solo sobrevivía el
    // último, dejando preset/from pegados en el default.
    setRange(
      preset === "custom"
        ? { preset, from: startYMD, to: endYMD }
        : { preset, from: "", to: "" }
    );
    setShowResume({
      startDate: moment(startYMD).format("MM/DD/YYYY"),
      endDate: moment(endYMD).format("MM/DD/YYYY"),
    });
    setUpdateCards(true);
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      dateStart: startYMD,
      dateEnd: endYMD,
    }));
    // Elegir un preset/rango reinicia cualquier ampliación incremental previa.
    setDateExpansions(0);
    setExpandedRange(null);
    expandBaseStartRef.current = null;
  };

  // Ampliación incremental del estado vacío: mueve el inicio del rango 30 días
  // más atrás por click (hasta MAX_DATE_EXPANSIONS), manteniendo el "hasta".
  // OJO: en esta vista el fetch de la tabla depende de `filters` (no de
  // showResume), así que hay que mover filters.dateStart/dateEnd —no solo
  // showResume— para que la tabla re-consulte realmente la ventana ampliada.
  const expandDateSearch = () => {
    if (dateExpansions >= MAX_DATE_EXPANSIONS) return;
    // Fijar el ancla en el primer click: el inicio actual del rango activo.
    if (expandBaseStartRef.current === null) {
      expandBaseStartRef.current = moment(showResume.startDate, "MM/DD/YYYY").startOf("day");
    }
    const nextExpansions = dateExpansions + 1;
    const newStart = expandBaseStartRef.current
      .clone()
      .subtract(nextExpansions * DATE_EXPANSION_DAYS, "days")
      .startOf("day");
    const newEnd = moment(showResume.endDate, "MM/DD/YYYY"); // mantiene el "hasta"

    setDateExpansions(nextExpansions);
    setExpandedRange({
      start: newStart.format("YYYY-MM-DD"),
      end: newEnd.format("YYYY-MM-DD"),
    });
    setShowResume({
      startDate: newStart.format("MM/DD/YYYY"),
      endDate: newEnd.format("MM/DD/YYYY"),
    });
    setUpdateCards(true); // recuenta las KPI cards para la ventana ampliada
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      dateStart: newStart.format("YYYY-MM-DD"),
      dateEnd: newEnd.format("YYYY-MM-DD"),
    }));
  };

  // Condición de carga — skeleton acorde al layout real (header + KPI cards + tabla).
  if (loading || isLoading) {
    return (
      <div className="w-100 all-orders-container mt-3" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        <div className="orders-header-container">
          <h2 className="orders-header-container__title">{t("Todos los pedidos")}</h2>
        </div>
        <SummaryCardsSkeleton count={totalCards.length} />
        <div className="all-orders-container__table">
          <TableSkeleton rows={Math.min(docsPerPage || 8, 10)} columns={headers.length + 1} />
        </div>
      </div>
    );
  }

  return (
    <div className="w-100 all-orders-container mt-3">
      {/* Vista de evidencia o contenido principal */}
      {viewEvidence ? (
        <EvidenceView
          selectedPackage={packageEvidence}
          completePackages={data}
          setViewEvidence={setViewEvidence}
          // Cancelar desde el detalle deja la lista vieja: se recarga con el
          // mismo disparador que usa el panel del menú de cada fila.
          onPackageChanged={() => setUpdateData(true)}
        />
      ) : (
        <>
          {/* Header: título + prefiltro de fechas (mismo control que admin Pedidos) */}
          <div className="orders-header-container">
            <h2 className="orders-header-container__title">{t("Todos los pedidos")}</h2>
            <div className="summary-cards-header__date-range">
              <DateRangeFilter
                value={expandedRange ? "custom" : datePreset}
                onChange={handleRangeChange}
                customRange={
                  expandedRange ||
                  (datePreset === "custom" && fromParam && toParam
                    ? { start: fromParam, end: toParam }
                    : null)
                }
              />
            </div>
          </div>
          {/* Cards de resumen horizontales compactas (mismo estilo que admin) */}
          <div className="summary-cards-container">
            {totalCards.map((item, index) => {
              const active = item.isAll
                ? !selectedCard
                : selectedCard?.key === item.key;
              const pct = cardsTotal > 0 ? Math.round((item.value / cardsTotal) * 100) : 0;
              return (
                <button
                  key={index}
                  type="button"
                  className={`summary-card summary-card--${item.tone}${
                    active ? " summary-card--active" : ""
                  }`}
                  // La tarjeta sigue respondiendo durante la carga de la tabla: con
                  // `pointer-events` apagado el clic no hacía nada y Clarity lo
                  // contaba como clic muerto (la mayoría de los usuarios de escritorio
                  // busca y hace clic en una tarjeta mientras la tabla recarga).
                  onClick={() => handleCardClick(item)}
                >
                  <div className="summary-card__top">
                    <span className="summary-card__icon" aria-hidden="true">
                      <span className="material-symbols-outlined">{item.icon}</span>
                    </span>
                    <span className="summary-card__value">{item.value.toLocaleString()}</span>
                  </div>
                  <div className="summary-card__label">{item.title}</div>
                  {!item.isAll && (
                    <div className="summary-card__bar">
                      <div
                        className="summary-card__bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {/* Tarjetas de resumen */}
          <div className="all-orders-container__table" id="table-container">
            <CustomTable
              title={""}
              headers={headers}
              data={tableData}
              totalDocs={totalDocs}
              page={null}
              actions={true}
              completeData={data}
              updateData={setUpdateData}
              docsPerPage={docsPerPage}
              loadingTable={loadingTable}
              pageSelected={null}
              setViewEvidence={setViewEvidence}
              setPackageEvidence={setPackageEvidence}
              filters={filters}
              setFilters={(value) => {
                setSelectedCard(null);
                setFilters({
                  ...value,
                  dateStart: moment(showResume.startDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
                  dateEnd: moment(showResume.endDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
                });
                // Filtro de columna → status/type a la URL; se limpia ?card=.
                setCardParam("");
                setStatusParam(
                  Array.isArray(value?.status) ? value.status.map(String) : []
                );
                setTypeParam(value?.type || "");
              }}
              infiniteScroll={true}
              // Load error: the notice replaces CustomTable's default "no
              // packages" block, so an empty list is never shown next to it.
              emptyState={
                companyId && loadError ? (
                  <div className="rounded-lg border border-border bg-status-error-soft w-100" role="alert">
                    <EmptyState
                      icon={<AlertTriangle className="text-status-error" />}
                      title={t("No pudimos cargar los pedidos")}
                      action={
                        <Button
                          variant="outline"
                          iconLeft={<RefreshCw size={16} aria-hidden />}
                          onClick={() => loadData(1, true)}
                        >
                          {t("Reintentar")}
                        </Button>
                      }
                    />
                  </div>
                ) : undefined
              }
              // CTA inline al activar la card "Listas para despachar" (status 100):
              // seleccionar todo lo cargado y abrir el selector de etiquetas.
              selectAllLabelsCTA={Boolean(selectedCard?.filter?.status?.includes(100))}
              // Props para el SearchBar integrado
              showSearchBar={!viewEvidence}
              searchBarProps={!viewEvidence ? {
                onChange: searchValue,
                loading: loadingTable,
                searchButton: true,
                onSearchButton: searchButtonAction,
                consolidated: true,
                value: query.query
              } : null}
            />
          </div>
          {/* Elemento observador para scroll infinito - fuera del contenedor de la tabla */}
          {data.length > 0 && !loadingTable && (
            <>
              {hasMore && (
                <div ref={observerTarget} className="infinite-scroll-observer">
                  {loadingMore && (
                    <div className="infinite-scroll-loading">
                      <Spinner />
                      <span className="ml-2">{t("Cargando más pedidos...")}</span>
                    </div>
                  )}
                </div>
              )}
              {!hasMore && (
                <div className="infinite-scroll-end">
                  <span>{t("No hay más pedidos para mostrar")}</span>
                </div>
              )}
            </>
          )}
          {/* Estado vacío: ampliación incremental del rango de fechas (espejo de
              /admin/pedidos). Aparece cuando la consulta terminó sin resultados.
              Exige companyId: sin él loadData no corre (early-return), así que el
              botón sería un no-op con label engañoso (admin cubre esto con
              isRequestComplete). */}
          {companyId && !loadingTable && !loadError && data.length === 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.625rem",
                padding: "0.5rem 1rem 2rem",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "0.8125rem",
                  color: "var(--text-tertiary)",
                }}
              >
                {dateExpansions > 0
                  ? t("Buscando desde el {{fecha}}", {
                      fecha: moment(showResume.startDate, "MM/DD/YYYY")
                        .locale("es")
                        .format("D MMM YYYY"),
                    })
                  : t("¿Buscas un pedido antiguo? Está fuera del rango de fechas actual.")}
              </p>
              {dateExpansions >= MAX_DATE_EXPANSIONS ? (
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                  }}
                >
                  {t("No se encontraron pedidos en el rango máximo de búsqueda.")}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={expandDateSearch}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    height: "2.25rem",
                    padding: "0 0.875rem",
                    border: "1px solid var(--color-primary)",
                    borderRadius: "var(--radius-md)",
                    backgroundColor: "transparent",
                    color: "var(--color-primary)",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: "1.125rem" }}>
                    manage_search
                  </span>
                  {t("Ampliar búsqueda 30 días más")}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
